import React, { useState, useEffect } from "react";
import { useLoader } from "../../../base/context/LoaderProvider";
import { ToastTypes, useMessage } from "../../../base/context/MessageProvider";
import { supplierAddressService } from "../../../service/admin/supplierAddressService";
import "./style.css";

export default function WarehouseManagement() {
  const loader = useLoader();
  const message = useMessage();

  const [warehouses, setWarehouses] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [formData, setFormData] = useState({
    label: "",
    warehousePrefix: "",
    sequencePattern: "YK",
    paddingDigits: 4,
    fullName: "",
    mobile: "",
    phone: "",
    address: "",
    provinceText: "",
    cityText: "",
    areaText: "",
    townText: "",
    postCode: "",
    districtCode: "",
    addressCode: "",
    isDefault: false,
  });

  useEffect(() => {
    fetchWarehouses();
  }, []);

  const fetchWarehouses = async () => {
    try {
      loader.showInlineLoader("Loading warehouses...", "small");
      const res = await supplierAddressService.getWarehouseAddresses();
      if (res.status?.code === 200) {
        setWarehouses(res.data || []);
      }
    } catch (error) {
      message.showToast("Failed to load warehouses", ToastTypes.ERROR);
    } finally {
      loader.hideLoader();
    }
  };

  const resetForm = () => {
    setFormData({
      label: "",
      warehousePrefix: "",
      sequencePattern: "YK",
      paddingDigits: 4,
      fullName: "",
      mobile: "",
      phone: "",
      address: "",
      provinceText: "",
      cityText: "",
      areaText: "",
      townText: "",
      postCode: "",
      districtCode: "",
      addressCode: "",
      isDefault: false,
    });
    setEditingId(null);
    setShowForm(false);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));
  };

  const handleEdit = (warehouse) => {
    setFormData({
      label: warehouse.label || "",
      warehousePrefix: warehouse.warehousePrefix || "",
      sequencePattern: warehouse.sequencePattern || "YK",
      paddingDigits: warehouse.paddingDigits || 4,
      fullName: warehouse.fullName || "",
      mobile: warehouse.mobile || "",
      phone: warehouse.phone || "",
      address: warehouse.address || "",
      provinceText: warehouse.provinceText || "",
      cityText: warehouse.cityText || "",
      areaText: warehouse.areaText || "",
      townText: warehouse.townText || "",
      postCode: warehouse.postCode || "",
      districtCode: warehouse.districtCode || "",
      addressCode: warehouse.addressCode || "",
      isDefault: warehouse.isDefault || false,
    });
    setEditingId(warehouse._id);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.label || !formData.warehousePrefix || !formData.fullName || 
        !formData.mobile || !formData.address || !formData.provinceText || 
        !formData.cityText || !formData.areaText || !formData.townText ||
        !formData.postCode || !formData.districtCode) {
      message.showToast("Please fill all required fields", ToastTypes.ERROR);
      return;
    }

    try {
      loader.showInlineLoader(editingId ? "Updating..." : "Creating...", "small");
      
      const payload = {
        ...formData,
        source: "warehouse",
        paddingDigits: parseInt(formData.paddingDigits) || 4,
      };

      let res;
      if (editingId) {
        res = await supplierAddressService.updateAddress(editingId, payload);
      } else {
        res = await supplierAddressService.createAddress(payload);
      }

      if (res.status?.code === 200 || res.status?.code === 201) {
        message.showToast(
          editingId ? "Warehouse updated successfully" : "Warehouse created successfully",
          ToastTypes.SUCCESS
        );
        resetForm();
        fetchWarehouses();
      }
    } catch (error) {
      message.showToast(error?.message || "Operation failed", ToastTypes.ERROR);
    } finally {
      loader.hideLoader();
    }
  };

  const handleSetDefault = async (id) => {
    try {
      loader.showInlineLoader("Setting as default...", "small");
      const res = await supplierAddressService.setDefaultAddress(id);
      if (res.status?.code === 200) {
        message.showToast("Default warehouse updated", ToastTypes.SUCCESS);
        fetchWarehouses();
      }
    } catch (error) {
      message.showToast("Failed to set default", ToastTypes.ERROR);
    } finally {
      loader.hideLoader();
    }
  };

  const handleDeleteClick = (warehouse) => {
    setDeleteConfirm(warehouse);
  };

  const handleDeleteCancel = () => {
    setDeleteConfirm(null);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm) return;
    
    try {
      loader.showInlineLoader("Deleting...", "small");
      const res = await supplierAddressService.deleteAddress(deleteConfirm._id);
      if (res.status?.code === 200) {
        message.showToast("Warehouse deleted", ToastTypes.SUCCESS);
        fetchWarehouses();
        setDeleteConfirm(null);
      }
    } catch (error) {
      message.showToast("Failed to delete", ToastTypes.ERROR);
    } finally {
      loader.hideLoader();
    }
  };

  return (
    <div className="wh-mgmt-container">
      <div className="wh-mgmt-header">
        <button 
          className="wh-btn primary"
          onClick={() => { resetForm(); setShowForm(true); }}
        >
          <i className="fas fa-plus"></i> Add Warehouse
        </button>
      </div>

      {/* Add/Edit Form */}
      {showForm && (
        <div className="wh-form-card">
          <div className="form-header">
            <h3>{editingId ? "Edit Warehouse" : "Add New Warehouse"}</h3>
            <button className="close-btn" onClick={resetForm}>
              <i className="fas fa-times"></i>
            </button>
          </div>
          
          <form onSubmit={handleSubmit}>
            <div className="form-section">
              <h4><i className="fas fa-tag"></i> Warehouse Info</h4>
              <div className="form-grid">
                <div className="form-group">
                  <label>Warehouse Name/Label *</label>
                  <input
                    type="text"
                    name="label"
                    value={formData.label}
                    onChange={handleInputChange}
                    placeholder="e.g., Main Warehouse, Shenzhen WH"
                  />
                </div>
                <div className="form-group">
                  <label>Warehouse Prefix *</label>
                  <input
                    type="text"
                    name="warehousePrefix"
                    value={formData.warehousePrefix}
                    onChange={handleInputChange}
                    placeholder="e.g., WH1, SZ"
                  />
                  <span className="hint">Used for order message prefix</span>
                </div>
                <div className="form-group">
                  <label>Sequence Pattern (Auto)</label>
                  <input
                    type="text"
                    value={`${formData.warehousePrefix || "?"}${String(1).padStart(formData.paddingDigits || 4, '0')}`}
                    readOnly
                    className="readonly-input"
                  />
                  <span className="hint">Auto-generated from prefix & digits</span>
                </div>
                <div className="form-group">
                  <label>Padding Digits</label>
                  <input
                    type="number"
                    name="paddingDigits"
                    value={formData.paddingDigits}
                    onChange={handleInputChange}
                    min="1"
                    max="8"
                  />
                  <span className="hint">Number of digits (4 = 0001)</span>
                </div>
              </div>
            </div>

            <div className="form-section">
              <h4><i className="fas fa-user"></i> Contact Details</h4>
              <div className="form-grid">
                <div className="form-group">
                  <label>Contact Name *</label>
                  <input
                    type="text"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleInputChange}
                    placeholder="Zhang San"
                  />
                </div>
                <div className="form-group">
                  <label>Mobile *</label>
                  <input
                    type="text"
                    name="mobile"
                    value={formData.mobile}
                    onChange={handleInputChange}
                    placeholder="15251667788"
                  />
                </div>
                <div className="form-group">
                  <label>Phone</label>
                  <input
                    type="text"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="0517-88990077"
                  />
                </div>
              </div>
            </div>

            <div className="form-section">
              <h4><i className="fas fa-map-marker-alt"></i> Address Details</h4>
              <div className="form-grid">
                <div className="form-group full-width">
                  <label>Street Address *</label>
                  <input
                    type="text"
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    placeholder="No. 699, Wangshang Road"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Province *</label>
                  <input
                    type="text"
                    name="provinceText"
                    value={formData.provinceText}
                    onChange={handleInputChange}
                    placeholder="Zhejiang Province"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>City *</label>
                  <input
                    type="text"
                    name="cityText"
                    value={formData.cityText}
                    onChange={handleInputChange}
                    placeholder="Hangzhou"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Area/District *</label>
                  <input
                    type="text"
                    name="areaText"
                    value={formData.areaText}
                    onChange={handleInputChange}
                    placeholder="Binjiang District"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Town *</label>
                  <input
                    type="text"
                    name="townText"
                    value={formData.townText}
                    onChange={handleInputChange}
                    placeholder="Changhe Town"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Post Code *</label>
                  <input
                    type="text"
                    name="postCode"
                    value={formData.postCode}
                    onChange={handleInputChange}
                    placeholder="310000"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>District Code *</label>
                  <input
                    type="text"
                    name="districtCode"
                    value={formData.districtCode}
                    onChange={handleInputChange}
                    placeholder="310107"
                    required
                  />
                </div>
              </div>
            </div>

            <div className="form-section">
              <label className="checkbox-wrapper">
                <input
                  type="checkbox"
                  name="isDefault"
                  checked={formData.isDefault}
                  onChange={handleInputChange}
                />
                <span>Set as default warehouse</span>
              </label>
            </div>

            <div className="form-actions">
              <button type="button" className="wh-btn secondary" onClick={resetForm}>
                Cancel
              </button>
              <button type="submit" className="wh-btn primary">
                {editingId ? "Update Warehouse" : "Create Warehouse"}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Warehouse List */}
      <div className="wh-list-section">
        <h3>Warehouses ({warehouses.length})</h3>
        
        {warehouses.length === 0 ? (
          <div className="empty-state">
            <i className="fas fa-warehouse"></i>
            <p>No warehouses found</p>
            <span>Add your first warehouse to get started</span>
          </div>
        ) : (
          <div className="wh-cards-grid">
            {warehouses.map((wh) => (
              <div key={wh._id} className={`wh-card ${wh.isDefault ? 'default' : ''}`}>
                <div className="wh-card-header">
                  <div className="wh-title">
                    <span className="wh-name">{wh.label || wh.fullName}</span>
                    {wh.isDefault && <span className="default-badge">Default</span>}
                  </div>
                  <div className="wh-prefix-badge">{wh.warehousePrefix || "N/A"}</div>
                </div>
                
                <div className="wh-card-body">
                  <div className="wh-info-row">
                    <i className="fas fa-hashtag"></i>
                    <span>Pattern: {wh.warehousePrefix}{String(1).padStart(wh.paddingDigits || 4, '0')}</span>
                  </div>
                  <div className="wh-info-row">
                    <i className="fas fa-sort-numeric-up"></i>
                    <span>Last Sequence: {wh.lastSequence || 0}</span>
                  </div>
                  <div className="wh-info-row">
                    <i className="fas fa-user"></i>
                    <span>{wh.fullName}</span>
                  </div>
                  <div className="wh-info-row">
                    <i className="fas fa-phone"></i>
                    <span>{wh.mobile}</span>
                  </div>
                  <div className="wh-info-row">
                    <i className="fas fa-map-marker-alt"></i>
                    <span>{wh.address}, {wh.cityText}, {wh.provinceText}</span>
                  </div>
                  <div className="wh-info-row">
                    <i className="fas fa-envelope"></i>
                    <span>{wh.postCode}</span>
                  </div>
                </div>

                <div className="wh-card-actions">
                  {!wh.isDefault && (
                    <button 
                      className="action-btn set-default"
                      onClick={() => handleSetDefault(wh._id)}
                      title="Set as Default"
                    >
                      <i className="fas fa-star"></i>
                    </button>
                  )}
                  <button 
                    className="action-btn edit"
                    onClick={() => handleEdit(wh)}
                    title="Edit"
                  >
                    <i className="fas fa-edit"></i>
                  </button>
                  <button 
                    className="action-btn delete"
                    onClick={() => handleDeleteClick(wh)}
                    title="Delete"
                  >
                    <i className="fas fa-trash"></i>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="delete-confirm-overlay" onClick={handleDeleteCancel}>
          <div className="delete-confirm-card" onClick={(e) => e.stopPropagation()}>
            <div className="delete-confirm-icon">
              <i className="fas fa-exclamation-triangle"></i>
            </div>
            <h3 className="delete-confirm-title">Delete Warehouse</h3>
            <p className="delete-confirm-message">
              Are you sure you want to delete <strong>"{deleteConfirm.label || deleteConfirm.fullName}"</strong>?
            </p>
            <p className="delete-confirm-warning">
              This action cannot be undone. All associated data will be permanently removed.
            </p>
            <div className="delete-confirm-actions">
              <button 
                className="delete-confirm-btn cancel"
                onClick={handleDeleteCancel}
              >
                Cancel
              </button>
              <button 
                className="delete-confirm-btn confirm"
                onClick={handleDeleteConfirm}
              >
                <i className="fas fa-trash"></i> Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
